import {
  redux_getAllProposals,
  redux_getProposal,
  redux_updateProposal,
  redux_createProposal,
  redux_proposalActionStarts,
  redux_proposalActionErrors,
  redux_resetProposalErrors,
} from '../../core/data/redux/modules/proposal/actions';

import { Dispatch } from 'redux';
import { makeApiRequest } from '../../utils/api-utils';
import { Proposal, ProposalCreateInput, ProposalUpdateInput, ProposalValidationErrors } from './types';

// Validation helper functions
const validateUUID = (id: string, fieldName: string): string | null => {
  if (!id || typeof id !== 'string' || id.length !== 36) {
    return `${fieldName} must be a valid UUID`;
  }
  return null;
};

const validateRequired = (value: any, fieldName: string): string | null => {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return `${fieldName} is required`;
  }
  return null;
};

const validateNumber = (value: any, fieldName: string, min = 0): string | null => {
  if (typeof value !== 'number' || value < min) {
    return `${fieldName} must be a number greater than or equal to ${min}`;
  }
  return null;
};

const validateProposalData = (data: ProposalCreateInput | ProposalUpdateInput): ProposalValidationErrors => {
  const errors: ProposalValidationErrors = {};
  
  // Validate required fields
  const projectIdError = validateRequired(data.project_id, 'Project ID');
  if (projectIdError) errors.project_id = projectIdError;

  const userIdError = validateRequired(data.user_id, 'User ID');
  if (userIdError) errors.user_id = userIdError;

  const projectLeadIdError = validateRequired(data.project_lead_id, 'Project Lead ID');
  if (projectLeadIdError) errors.project_lead_id = projectLeadIdError;

  const quotationDetailsError = validateRequired(data.quotation_details, 'Quotation Details');
  if (quotationDetailsError) errors.quotation_details = quotationDetailsError;

  const priceError = validateNumber(data.price, 'Price', 0);
  if (priceError) errors.price = priceError;

  const overviewError = validateRequired(data.overview, 'Overview'); if (overviewError) errors.overview = overviewError;

  const scopeOfWorkError = validateRequired(data.scope_of_work, 'Scope of Work');
  if (scopeOfWorkError) errors.scope_of_work = scopeOfWorkError;

  // Validate UUIDs
  if (data.project_id) {
    const projectIdUUIDError = validateUUID(data.project_id, 'Project ID');
    if (projectIdUUIDError) errors.project_id = projectIdUUIDError;
  }

  if (data.user_id) {
    const userIdUUIDError = validateUUID(data.user_id, 'User ID');
    if (userIdUUIDError) errors.user_id = userIdUUIDError;
  }

  if (data.project_lead_id) {
    const projectLeadIdUUIDError = validateUUID(data.project_lead_id, 'Project Lead ID');
    if (projectLeadIdUUIDError) errors.project_lead_id = projectLeadIdUUIDError;
  }

  return errors;
};

export const getAllProposals = () => async (dispatch: Dispatch) => {
  dispatch(redux_proposalActionStarts());
  try {
    const response = await makeApiRequest(`/proposals/get-all`);
    const responseDto = response;
    if (responseDto.error) {
      dispatch(redux_proposalActionErrors(responseDto.error));
    } else {
      dispatch(redux_getAllProposals(responseDto.data));
    }
  } catch (error) {
    const errorMessage = (error as any)?.message || 'Error fetching proposals';
    dispatch(redux_proposalActionErrors(errorMessage));
  }
};

export const getProposalById = (id: string, pinCode?: string) => async (dispatch: Dispatch) => {
  dispatch(redux_proposalActionStarts());
  try {
    // Validate UUID format
    const idError = validateUUID(id, 'Proposal ID');
    if (idError) {
      throw new Error(idError);
    }

    let url = `/proposals/get-by-id/${id}`;
    if (pinCode) {
      // Validate PIN code format
      if (!pinCode || typeof pinCode !== 'string' || pinCode.length !== 6) {
        throw new Error('Invalid PIN code format. PIN must be 6 characters long.');
      }
      url += `?pin_code=${pinCode}`;
    }

    const response = await makeApiRequest(url);
    const responseDto = response;
    if (responseDto.error) {
      dispatch(redux_proposalActionErrors(responseDto.error));
    } else {
      dispatch(redux_getProposal(responseDto.data));
    }
  } catch (error) {
    const errorMessage = (error as any)?.message || 'Error fetching proposal';
    dispatch(redux_proposalActionErrors(errorMessage));
  }
};

export const createProposal = (proposalData: ProposalCreateInput, setModalOpen?: (value: boolean) => void) => async (dispatch: Dispatch) => {
  dispatch(redux_proposalActionStarts());
  try {
    // Validate proposal data
    const validationErrors = validateProposalData(proposalData);
    const errorKeys = Object.keys(validationErrors);
    
    if (errorKeys.length > 0) {
      const errorMessages = errorKeys.map(key => validationErrors[key as keyof ProposalValidationErrors]).join(', ');
      throw new Error(`Validation failed: ${errorMessages}`);
    }

    const response = await makeApiRequest(`/proposals/add`, {
      method: 'POST',
      body: JSON.stringify(proposalData),
    });

    const responseDto = response;
    if (responseDto.error) {
      dispatch(redux_proposalActionErrors(responseDto.error));
    } else {
      dispatch(redux_createProposal(responseDto.data));
      if (setModalOpen) {
        setModalOpen(false);
      }
    }
  } catch (error) {
    const errorMessage = (error as any)?.message || 'Error creating proposal';
    dispatch(redux_proposalActionErrors(errorMessage));
    // Don't close modal on error to preserve form data
  }
};

export const updateProposal = (id: string, proposalData: ProposalUpdateInput, setModalOpen?: (value: boolean) => void) => async (dispatch: Dispatch) => {
  dispatch(redux_proposalActionStarts());
  try {
    // Validate UUID format
    const idError = validateUUID(id, 'Proposal ID');
    if (idError) {
      throw new Error(idError);
    }

    // Validate proposal data
    const validationErrors = validateProposalData(proposalData);
    const errorKeys = Object.keys(validationErrors);
    
    if (errorKeys.length > 0) {
      const errorMessages = errorKeys.map(key => validationErrors[key as keyof ProposalValidationErrors]).join(', ');
      throw new Error(`Validation failed: ${errorMessages}`);
    }

    const response = await makeApiRequest(`/proposals/update/${id}`, {
      method: 'PUT',
      body: JSON.stringify(proposalData),
    });

    const responseDto = response;
    if (responseDto.error) {
      dispatch(redux_proposalActionErrors(responseDto.error));
    } else {
      dispatch(redux_updateProposal(responseDto.data));
      if (setModalOpen) {
        setModalOpen(false);
      }
    }
  } catch (error) {
    const errorMessage = (error as any)?.message || 'Error updating proposal';
    dispatch(redux_proposalActionErrors(errorMessage));
    // Don't close modal on error to preserve form data
  }
};

export const deleteProposal = (id: string) => async (dispatch: Dispatch) => {
  dispatch(redux_proposalActionStarts());
  try {
    // Validate UUID format
    const idError = validateUUID(id, 'Proposal ID');
    if (idError) {
      throw new Error(idError);
    }

    const response = await makeApiRequest(`/proposals/delete/${id}`, {
      method: 'DELETE',
    });

    const responseDto = response;
    if (responseDto.error) {
      dispatch(redux_proposalActionErrors(responseDto.error));
    } else {
      dispatch(getAllProposals() as any);
    }
  } catch (error) {
    const errorMessage = (error as any)?.message || 'Error deleting proposal';
    dispatch(redux_proposalActionErrors(errorMessage));
  }
};
